import { useEffect, useState } from 'react';
import { api, ApiError } from '../lib/api';
import { RiskBadge } from '../components/RiskBadge';
import { Stat } from '../components/Stat';
import { Skel } from '../components/Skel';
import { Banner } from '../components/Banner';
import { Crumbs } from '../components/Crumbs';
import type { IpEntity } from '../data/types';

interface IPProps {
  slug: string;
}

/**
 * Карточка индивидуального предпринимателя.
 *
 * Endpoint: GET /v1/ip/{slug}
 * Slug формат: {фамилия}-{имя}-{отчество}-{ИНН12} (lowercase, латиница).
 *
 * Layout: Crumbs + header (ФИО + ИНН + ОГРНИП + risk + bankruptcy flag) +
 *  два блока: Основные сведения + Сигналы.
 *
 * NB: ИП-данные часто менее богатые чем для ЮЛ — финансовые, директоры
 * не применимы. Показываем что есть из ЕГРИП ФНС.
 */
export function IP({ slug }: IPProps) {
  const [ip, setIp] = useState<IpEntity | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setIp(null);
    setError(null);
    const ac = new AbortController();
    api
      .ip(slug, { signal: ac.signal })
      .then(setIp)
      .catch((e) => {
        if (e.name !== 'AbortError') {
          setError(e instanceof ApiError ? `API ${e.status}` : String(e));
        }
      });
    return () => ac.abort();
  }, [slug]);

  if (error) {
    return (
      <main className="container" style={{ paddingTop: 32 }}>
        <Crumbs items={[{ label: 'Главная', href: '#/' }, { label: 'Поиск', href: '#/search' }, { label: slug }]} />
        <Banner tone="risk" title="Не удалось загрузить ИП">
          {error}
        </Banner>
      </main>
    );
  }

  if (!ip) {
    return (
      <main className="container" style={{ paddingTop: 32 }}>
        <Crumbs items={[{ label: 'Главная', href: '#/' }, { label: 'Поиск', href: '#/search' }, { label: slug }]} />
        <Skel height={32} width="60%" style={{ marginTop: 16 }} />
        <Skel height={20} width="40%" style={{ marginTop: 8 }} />
        <Skel count={3} height={64} radius={12} style={{ marginTop: 24 }} />
      </main>
    );
  }

  const isTerminated = ip.status !== 'active';
  return (
    <main className="container" style={{ paddingTop: 32 }}>
      <Crumbs
        items={[
          { label: 'Главная', href: '#/' },
          { label: 'Поиск', href: '#/search' },
          { label: ip.full_name },
        ]}
      />

      <header style={{ marginTop: 16, marginBottom: 24 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
          <span
            style={{
              fontSize: 11,
              padding: '2px 8px',
              borderRadius: 4,
              background: 'var(--bg, #fbfaf7)',
              color: 'var(--muted, #6b7287)',
              border: '1px solid var(--border, #e6e2d6)',
              textTransform: 'uppercase',
              letterSpacing: '0.04em',
              fontWeight: 600,
            }}
          >
            ИП
          </span>
        </div>
        <h1
          style={{
            fontSize: 'clamp(22px, 3vw, 32px)',
            margin: 0,
            color: 'var(--navy-700, #0b1736)',
            overflowWrap: 'anywhere',
          }}
        >
          {ip.full_name}
        </h1>
        <div
          style={{
            display: 'flex',
            gap: 12,
            alignItems: 'center',
            flexWrap: 'wrap',
            marginTop: 12,
            fontSize: 13,
          }}
        >
          <code style={pillStyle}>ИНН {ip.inn}</code>
          <code style={pillStyle}>ОГРНИП {ip.ogrnip}</code>
          {ip.risk_level && <RiskBadge level={ip.risk_level} score={ip.risk_score} />}
          {isTerminated && (
            <span style={inactivePill}>
              {ip.status === 'terminated' ? 'Прекращена' : ip.status}
            </span>
          )}
          {ip.has_bankruptcy_proceedings && (
            <span style={alertPill}>⚠ Банкротство</span>
          )}
        </div>
      </header>

      {/* Stats grid */}
      <section
        aria-label="Сигналы"
        style={{
          display: 'grid',
          gap: 16,
          gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
          marginBottom: 24,
        }}
      >
        <Stat label="Регион" value={ip.region_name ?? ip.region_code ?? '—'} format={false} />
        <Stat
          label="Активные суды"
          value={ip.active_lawsuits_count}
          note={ip.active_lawsuits_count > 0 ? 'Требует внимания' : undefined}
        />
        <Stat
          label="Исп. производства"
          value={ip.active_enforcement_count}
          note={ip.active_enforcement_count > 0 ? 'Требует внимания' : undefined}
        />
        <Stat
          label="Возраст бизнеса"
          value={`${calcAgeYears(ip.registered_at)} лет`}
          format={false}
        />
      </section>

      {/* Основные сведения */}
      <section
        aria-label="Основные сведения"
        style={{
          background: 'var(--surface, #ffffff)',
          border: '1px solid var(--border, #e6e2d6)',
          borderRadius: 12,
          padding: 20,
          marginBottom: 24,
        }}
      >
        <h2 style={blockTitle}>Основные сведения</h2>
        <Row label="Дата регистрации" value={fmtDate(ip.registered_at)} />
        <Row label="Статус" value={ip.status === 'active' ? 'Действующий ИП' : ip.status} />
        {ip.terminated_at && (
          <Row
            label="Дата прекращения"
            value={`${fmtDate(ip.terminated_at)}${ip.termination_reason ? ` (${ip.termination_reason})` : ''}`}
          />
        )}
        {ip.okved_main_code && (
          <Row
            label="ОКВЭД"
            value={`${ip.okved_main_code} · ${ip.okved_main_name ?? ''}`}
          />
        )}
        <Row label="Регион" value={ip.region_name ?? ip.region_code ?? '—'} />
        {ip.city && <Row label="Город" value={ip.city} />}
        {ip.birth_year && <Row label="Год рождения" value={String(ip.birth_year)} />}
        {ip.tax_regime && (
          <Row
            label="Налоговый режим"
            value={`${ip.tax_regime}${ip.tax_regime_since ? ` (с ${fmtDate(ip.tax_regime_since)})` : ''}`}
          />
        )}
        {ip.tax_office_code && <Row label="Код ИФНС" value={ip.tax_office_code} />}
      </section>

      {/* Bankruptcy banner */}
      {ip.has_bankruptcy_proceedings && (
        <Banner tone="risk" title="Процедура банкротства">
          В отношении ИП ведётся процедура банкротства. Заключение договоров требует
          дополнительной осмотрительности.
        </Banner>
      )}

      {/* Disclaimer */}
      <p
        style={{
          marginTop: 32,
          fontSize: 12,
          color: 'var(--muted, #6b7287)',
          textAlign: 'center',
        }}
      >
        Данные ЕГРИП (ФНС России){ip.last_synced_at ? ` · обновлено ${fmtDate(ip.last_synced_at)}` : ''}
      </p>
    </main>
  );
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div
      style={{
        display: 'flex',
        gap: 12,
        flexWrap: 'wrap',
        padding: '6px 0',
        borderBottom: '1px solid var(--border, #e6e2d6)',
        fontSize: 14,
      }}
    >
      <div style={{ color: 'var(--muted, #6b7287)', minWidth: 140, flexShrink: 0 }}>{label}</div>
      <div style={{ flex: 1, minWidth: 0, overflowWrap: 'anywhere' }}>{value}</div>
    </div>
  );
}

const pillStyle: React.CSSProperties = {
  background: 'var(--surface-quiet, #f5f3ec)',
  padding: '4px 8px',
  borderRadius: 4,
  fontFamily: 'JetBrains Mono, monospace',
};
const inactivePill: React.CSSProperties = {
  background: '#fff5d9',
  color: '#7a5a10',
  padding: '4px 10px',
  borderRadius: 999,
  fontWeight: 600,
  border: '1px solid #e6c870',
};
const alertPill: React.CSSProperties = {
  background: '#fde2e2',
  color: '#a31919',
  padding: '4px 10px',
  borderRadius: 999,
  fontWeight: 600,
  border: '1px solid #e89191',
};
const blockTitle: React.CSSProperties = {
  fontFamily: 'Fraunces, Georgia, serif',
  fontSize: 18,
  margin: '0 0 12px',
  color: 'var(--navy-700, #0b1736)',
};

function calcAgeYears(iso: string): string {
  const ms = Date.now() - new Date(iso).getTime();
  return (ms / (365.25 * 24 * 60 * 60 * 1000)).toFixed(1);
}

function fmtDate(iso: string | null): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('ru-RU');
}
